<script setup lang="ts">
import { TheHumanBody } from '@ilteducation/games';
import { DialogCloseButton } from '@ilteducation/components';
import { onMounted, ref } from 'vue';
import useKeywordsStore from '@/store/keywords';
import { useLocale } from '@/plugins/translation';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import type { Video } from '@ilteducation/videos';

const props = defineProps<{
  video: Video;
}>();

const { sendAnalyticsEvent } = useClientAnalytics();

const { t, l } = useLocale();

const showModal = defineModel({ default: false });
const showKeywordInfo = ref(false);

const { getByKey, loadByKey } = useKeywordsStore();

const theHumanBody = ref<InstanceType<typeof TheHumanBody> | null>(null);

const keywordLabel = ref<string | undefined>();
const keyWordDescription = ref<string | undefined>();

const showKeywordDescription = async (kw: string) => {
  keywordLabel.value = undefined;
  keyWordDescription.value = undefined;
  showKeywordInfo.value = true;
  let keyword = getByKey(kw) || getByKey(kw.toLowerCase());
  if (!keyword) {
    keyword = await loadByKey(kw);
  }
  keywordLabel.value = kw;
  keyWordDescription.value = keyword?.description ?? '';
  //return keyword?.translations.description[props.supportLanguage || getMarketLanguage()]?.translation;
};

const closeKeywordDialog = () => {
  showKeywordInfo.value = false;
  keywordLabel.value = undefined;
  keyWordDescription.value = undefined;
  theHumanBody.value?.closeBodyPart();
};

const closeModal = () => {
  showModal.value = false;
  closeKeywordDialog();
};

const onShowModal = () => {
  sendAnalyticsEvent('AppUserEventType', {
    eventName: 'open_human_body_exercise',
    metadata: {
      videoId: props.video.id,
      title: props.video.title,
    },
  });
};

onMounted(onShowModal);
</script>

<template>
  <v-dialog
    v-model="showModal"
    height="100vh"
    :aria-label="t(l.games.the_human_body.start_text)"
    @keydown.esc="closeModal"
    @click:outside="closeModal"
  >
    <v-card max-width="100%" class="pa-2 rounded-lg">
      <TheHumanBody
        ref="theHumanBody"
        @bodypart-clicked="showKeywordDescription"
        @info-panel-closed="closeKeywordDialog"
      />
      <v-card v-if="showKeywordInfo" class="keyword-card pa-5 rounded-lg" min-width="300px" max-width="500px">
        <v-progress-circular
          v-if="keywordLabel === undefined"
          indeterminate
          color="primary"
          :aria-label="t(l.label_loading)"
        ></v-progress-circular>
        <h1>{{ keywordLabel }}</h1>
        <p>{{ keyWordDescription }}</p>
        <DialogCloseButton variant="flat" size="small" @click="closeKeywordDialog" />
      </v-card>
      <DialogCloseButton variant="flat" color="white" @click="closeModal" />
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.keyword-card {
  position: absolute;
  top: 100px;
  right: 100px;
  z-index: 10;
}
</style>
